import { filter } from 'rxjs/operators';
import { Component, OnInit, afterNextRender, signal } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { IsLoggedIn } from '@core/entity/auth/state/auth.selectors';
import { Router, NavigationEnd, NavigationStart, RouteConfigLoadStart, RouteConfigLoadEnd, NavigationCancel } from '@angular/router';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { PreLoaderComponent } from '@shared/pre-loader/pre-loader.component';
import { TopbarComponent } from '../topbar/topbar.component';
import { CommonModule } from '@angular/common';
import { PlatformService } from '@core/services/platform.service';
import { SidePanelsComponent } from '../sidepanels/sidepanels.component';
import { environment } from '@env/environment';
import { AuthTokenKey } from '@shared/local-storage.key';
import { SKYNET_SCRIPT_ID } from '@common/skynet-locale';
// import { TwoStepDepositDataService } from '@core/services/two-step-deposit-data.service';


@Component({
  selector: 'app-base-inner',
  imports: [CommonModule, PreLoaderComponent, TopbarComponent, SidePanelsComponent],
  templateUrl: './base-inner.component.html',
  styleUrls: ['./base-inner.component.scss']
})
export class BaseInnerComponent implements OnInit {
  maintenanceTime: string | null = null;
  hasRewards: boolean;
  excludeRoute: string | string[] = null;
  currentUrl: string = null;
  isContentPage: boolean;

  // Floating RTP widget (ported from template-02). Shown on qp2a–qp2d.
  projectCode = environment.projectCode.toLocaleLowerCase();
  isQP2 = ['qp2a', 'qp2b', 'qp2c', 'qp2d'].includes(this.projectCode);
  rtpDomain = (environment as any).rtpDomain;
  liveChatExists = false;
  showRtp = true;
  // The RTP page is only useful to logged-in players (AIO-82553), so the widget waits for login.
  isLoggedIn = signal(false);

  constructor(
    private router: Router,
    public loadingBar: LoadingBarService,
    private platformService: PlatformService,
    private store: Store,
    // private twoStepDepositDataService: TwoStepDepositDataService,
  ) {
    // Read login state only after hydration: SSR always renders logged-out, so subscribing
    // earlier would make the first client render differ from the server markup (NG0500).
    afterNextRender(() => {
      this.store.pipe(select(IsLoggedIn)).subscribe(loggedIn => this.isLoggedIn.set(loggedIn));
    });
  }

  ngOnInit() {
    if (this.platformService.isBrowser()) {
      this.maintenanceTime = localStorage.getItem('maintenance');

      /* Nudge the RTP widget above the chat bubble once a chat script is present. Two ids to
         watch since AIO-82885: BO picks the provider per brand, so exactly one of them ever
         appears. Both are appended to document.head — LiveChat/Crisp by AppComponent's
         jQuery, Skynet by SkynetService.install(), beside loadClarity()'s tag. */
      const chatScriptIds = ['live-chat-script', SKYNET_SCRIPT_ID];
      const chatScriptPresent = () => chatScriptIds.some(id => document.getElementById(id) !== null);

      this.liveChatExists = chatScriptPresent();
      if (!this.liveChatExists) {
        const observer = new MutationObserver(() => {
          if (chatScriptPresent()) {
            this.liveChatExists = true;
            observer.disconnect();
          }
        });
        observer.observe(document.head, { childList: true, subtree: true });
      }
    }
    // Percentage just in case needed!
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // this.loadingBar.start();
        // const lsStepTwoDeposit =
        // this.twoStepDepositDataService.getLSStepTwoDeposit();

        // if (lsStepTwoDeposit) {
        //   this.loadingBar.complete();
        // }
      }
      if (event instanceof RouteConfigLoadStart) {
        this.loadingBar.increment(35);
      }
      if (event instanceof RouteConfigLoadEnd) {
        this.loadingBar.increment(75);
      }
      if (event instanceof NavigationEnd) {
        this.checkUrl();
        if (this.platformService.isBrowser()) {
          this.maintenanceTime = localStorage.getItem('maintenance');
        }
        this.loadingBar.complete();
      }
      if (event instanceof NavigationCancel) {
        this.loadingBar.complete();
      }
    });

  }

  show() {
    const url = this.router.url.split('?')[0].split('#')[0];
    // Hide the app chrome on the landing / thank-you pages. Landing lives at '/'
    // (path '' -> LandingComponent); the legacy '/landing' route redirects to '/',
    // so checking '/' is enough.
    // include "thank-you" because PG will redirect back with querystring. Eg. /thank-you?status=success
    if (url === '/' || url === '/thank-you' || url.includes('thank-you')) {
      return false;
    } else {
      return true;
    }
  }

  checkUrl() {
    this.excludeRoute = ['/about-us', '/faq', '/contact-us', '/terms-conditions', '/privacy-policy', '/responsible-gaming'];
    this.currentUrl = this.router.url;
    if (this.excludeRoute.includes(this.currentUrl) || this.currentUrl.includes('/info-center')) {
      this.isContentPage = true;
    } else {
      this.isContentPage = false;
    }
  }

  onCloseRTP(event: Event) {
    event.stopPropagation();
    this.showRtp = false;
  }

  onRTP() {
    const lang = localStorage.getItem('language_code') ?? environment.defaultLanguage;
    const country = localStorage.getItem('country_code') ?? environment.defaultCountryCode;
    let url = this.rtpDomain + '?project_code=' + this.projectCode + '&lang=' + lang + '&country=' + country;
    // Logged-in players forward the current domain + auth tokens so RTP can validate server-side
    // (a still-valid token keeps the link domain; expired/guest falls back to the site_url table).
    const rawToken = localStorage.getItem(AuthTokenKey);
    try {
      const { access_token, plaintext_token } = rawToken ? JSON.parse(rawToken) : {} as any;
      if (access_token && plaintext_token) {
        url += '&domain=' + window.location.origin
             + '&access_token=' + encodeURIComponent(access_token)
             + '&plaintext_token=' + encodeURIComponent(plaintext_token);
      }
    } catch { /* malformed token → treat as guest */ }
    window.location.href = url;
  }
}
